import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/contexts/I18nContext";
import { DEFAULT_LANG, type Lang } from "@/i18n";

const SITE_URL = "https://power-toefl.com";

export interface SeoKeywordMapping {
  primary_keyword: string;
  secondary_keywords: string[];
  title_suggestion: string;
  meta_description_suggestion: string;
  h1_suggestion: string;
  intro_copy: string | null;
  internal_link_anchors: string[];
  faq: Array<{ q: string; a: string }> | null;
  search_intent: string | null;
}

const cache = new Map<string, SeoKeywordMapping | null>();

function buildUrl(path: string, lang: Lang): string {
  const prefix = lang === DEFAULT_LANG ? "" : `/${lang}`;
  return `${SITE_URL}${prefix}${path === "/" ? "" : path}`;
}

/**
 * Fetch a row from `seo_keyword_mappings` for the current page+locale.
 * Returns null while loading or if no mapping exists (caller should fall back
 * to existing static SEO copy).
 */
export function useSeoKeywordMapping(path: string): SeoKeywordMapping | null {
  const { lang } = useI18n();
  const url = buildUrl(path, lang);
  const cacheKey = `${url}::${lang}`;
  const [data, setData] = useState<SeoKeywordMapping | null>(
    cache.has(cacheKey) ? cache.get(cacheKey)! : null,
  );

  useEffect(() => {
    if (cache.has(cacheKey)) {
      setData(cache.get(cacheKey)!);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: row } = await supabase
        .from("seo_keyword_mappings")
        .select(
          "primary_keyword, secondary_keywords, title_suggestion, meta_description_suggestion, h1_suggestion, intro_copy, internal_link_anchors, faq, search_intent",
        )
        .eq("url", url)
        .eq("locale", lang)
        .maybeSingle();
      if (cancelled) return;
      const value = (row as SeoKeywordMapping | null) ?? null;
      cache.set(cacheKey, value);
      setData(value);
    })();
    return () => {
      cancelled = true;
    };
  }, [url, lang, cacheKey]);

  return data;
}
